/**
 * Pack Features Section
 *
 * Checkbox list of all active features for a pack.
 * Toggle adds/removes features; expanding shows option configuration.
 */

import { useState, type ReactElement } from 'react';
import {
  useAddPackItem,
  useRemovePackItem,
  useUpdatePackItem,
} from '../../hooks/useAdminPacks';
import { useAdminFeatures } from '../../hooks/useAdminFeatures';
import { PackFeatureRow } from './PackFeatureRow';
import { MissingRuntimeConfigBanner } from './MissingRuntimeConfigBanner';
import { useMissingRuntimeConfig } from '../../hooks/useMissingRuntimeConfig';
import type { Feature, PackItem } from '../../schemas/admin.schemas';
import { buildValues, computeSparseOverrides } from '../../utils/featureOptionValues';

interface PackFeaturesSectionProps {
  packId: string;
  packName?: string;
  items: PackItem[];
  linkedProjectCount: number;
}

export function PackFeaturesSection({
  packId,
  packName,
  items,
  linkedProjectCount,
}: PackFeaturesSectionProps): ReactElement {
  const { data: allFeatures } = useAdminFeatures(false);
  const addItem = useAddPackItem();
  const removeItem = useRemovePackItem();
  const updateItem = useUpdatePackItem();
  const { data: missing } = useMissingRuntimeConfig({ packId });
  const missingByFeature = new Map((missing ?? []).map((m) => [m.feature_id, m]));

  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [optionValues, setOptionValues] = useState<Record<string, unknown>>({});

  const features = allFeatures ?? [];
  const itemMap = new Map<string, PackItem>();
  for (const item of items) {
    itemMap.set(item.feature_id, item);
  }

  // Compute dependency relationships for badge display
  const requiredByMap = new Map<string, string[]>();
  for (const f of features) {
    if (!itemMap.has(f.id)) continue;
    for (const depName of f.dependencies) {
      const depFeature = features.find((df) => df.name === depName);
      if (depFeature) {
        const existing = requiredByMap.get(depFeature.id) ?? [];
        existing.push(f.display_name);
        requiredByMap.set(depFeature.id, existing);
      }
    }
  }

  const handleToggle = (featureId: string): void => {
    const existing = itemMap.get(featureId);
    if (existing) {
      const feature = features.find((f) => f.id === featureId);
      const dependentNames = requiredByMap.get(featureId);
      if (dependentNames && dependentNames.length > 0) {
        const confirmed = window.confirm(
          `Removing ${feature?.display_name ?? 'this feature'} will also remove: ${dependentNames.join(', ')}. Continue?`
        );
        if (!confirmed) return;
      }
      removeItem.mutate({ packId, itemId: existing.id });
    } else {
      addItem.mutate({ packId, featureId });
    }
  };

  const handleExpand = (feature: Feature): void => {
    if (expandedId === feature.id) { setExpandedId(null); return; }
    setOptionValues(buildValues(feature, itemMap.get(feature.id)?.options));
    setExpandedId(feature.id);
  };

  const handleSaveOptions = (feature: Feature): void => {
    const item = itemMap.get(feature.id);
    if (!item) return;
    const overrides = computeSparseOverrides(optionValues, feature);
    updateItem.mutate({ packId, itemId: item.id, options: overrides ?? {} });
    setExpandedId(null);
  };

  return (
    <div className="project-features">
      <h3 className="project-features__title">Pack Features</h3>
      <MissingRuntimeConfigBanner items={missing ?? []} linkedProjectCount={linkedProjectCount} />
      {linkedProjectCount > 0 && (
        <div className="admin-page__sync-message" role="status">
          Changes will trigger image rebuilds for {String(linkedProjectCount)} linked
          project{linkedProjectCount !== 1 ? 's' : ''} on next launch.
        </div>
      )}
      {features.length === 0 ? (
        <p className="project-features__empty">
          No features available. Create features in the Feature Registry first.
        </p>
      ) : (
        <ul className="project-features__list project-features__list--pack">
          <li className="project-features__header">
            <span className="project-features__header-assigned">In Pack</span>
            <span className="project-features__header-feature">Feature</span>
            <span className="project-features__header-version">Version</span>
            <span className="project-features__header-category">Category</span>
            <span className="project-features__header-gate">Gate</span>
            <span className="project-features__header-options" />
          </li>
          {features.map((feature) => (
            <PackFeatureRow
              key={feature.id}
              feature={feature}
              packId={packId}
              {...(packName ? { packName } : {})}
              inPack={itemMap.has(feature.id)}
              isExpanded={expandedId === feature.id}
              toggleDisabled={addItem.isPending || removeItem.isPending}
              requiredBy={requiredByMap.get(feature.id)}
              missingItem={missingByFeature.get(feature.id)}
              optionValues={expandedId === feature.id
                ? optionValues : buildValues(feature, itemMap.get(feature.id)?.options)}
              onToggle={() => { handleToggle(feature.id); }}
              onExpand={() => { handleExpand(feature); }}
              onOptionChange={(key, value) => { setOptionValues((prev) => ({ ...prev, [key]: value })); }}
              onSaveOptions={() => { handleSaveOptions(feature); }}
            />
          ))}
        </ul>
      )}
    </div>
  );
}
